// equipment_fuelings transport for the authenticated equipment record page.
//
// EquipmentDetail owns the inline field save, three checklist edits and one
// imperative re-read against equipment_fuelings. The component keeps every
// decision about WHEN to call them — the optimistic local patch, the 800ms
// debounce, the blur/pagehide/visibility flushes and the notice text. This
// module owns the transport, and since migration 223 every edit is one
// SECURITY DEFINER RPC that changes the row AND records its equipment.item
// Activity row in the same transaction:
//
//   setFuelingFillupItem              -> set_equipment_fueling_check
//   setFuelingIntervalTask            -> set_equipment_fueling_interval_task
//   removeFuelingIntervalEntryAudited -> remove_equipment_fueling_interval_entry
//   patchFuelingField                 -> patch_equipment_fueling_field
//
// So an edit either lands with its history or not at all; the page never
// logs Activity for these itself. The server derives the actor, the equipment
// and the before/after from the locked row; the client sends only the intent.
//
// RETRY IDENTITY. Each user intent gets one request id. A transport failure
// (no response: the request may or may not have committed) is resent with the
// SAME id; the server answers a recorded id with {replayed:true} and changes
// nothing, so a lost response can neither drop nor duplicate history. A
// database refusal is final and is never resent.
//
// ORDER. Calls for one fueling row run one at a time, in click order, so a
// retry is resolved before the next intent for that row is sent.
//
// OFFLINE QUEUE, STATED PRECISELY. equipment_fuelings is not an offline-OWNED
// TABLE: src/lib/offlineForms.ts _REGISTRY declares a `table` for fuel_supplies,
// poultry_dailys, cattle_dailys, sheep_dailys and pig_dailys and nothing else.
// But the queue does reach this table — src/lib/offlineRpcForms.ts registers the
// equipment_fueling form against the submit_equipment_fueling RPC, which inserts
// an equipment_fuelings row (and, since 223, its record.created Activity row,
// keyed by client_submission_id so a replay adds none). These edits all act on
// rows that already exist, so none of them competes with the queue.

import {fireActivityChangeEvent} from './activityApi.js';
import {newClientSubmissionId} from './clientSubmissionId.js';
import type {FuelingReadingRow} from './equipment.js';
import type {SupabaseClient, SupabaseErrorLike} from './supabaseTypes.js';

// The imperative post-write reading re-read. `data` is the projection the
// chain asks for, which is exactly what currentReadingFromFuelings consumes.
export type FuelingReadingsResult = {data: FuelingReadingRow[] | null; error: SupabaseErrorLike | null};

// Fresh reading columns for one piece of equipment. This is an imperative
// POST-WRITE re-read, not a render read: it runs after a fueling save and
// before the page reload so the derived current reading is computed from the
// row just written. EQUIPMENT_FLEET_CORE reads this table too; that is a
// separate, coarser owner, not a duplicate of this one.
export async function listFuelingReadings(sb: SupabaseClient, equipmentId: string): Promise<FuelingReadingsResult> {
  const {data, error} = await sb
    .from('equipment_fuelings')
    .select('hours_reading,km_reading')
    .eq('equipment_id', equipmentId);
  return {data, error};
}

// What every audited edit resolves with. `value` is the stored value after the
// call (the whole array for checklist edits, the column value for a field
// edit), for the page to settle its optimistic copy on.
export type AuditedFuelingWrite =
  | {ok: true; changed: boolean; replayed: boolean; value: unknown}
  | {ok: false; error: string};

export type FuelingRef = {equipmentId: string; fuelingId: string};
export type FuelingChecklistItem = {id: string | number; label?: string | null};

// One recorded interval completion, as far as the identity check reads it.
export type FuelingIntervalEntry = {
  [key: string]: unknown;
  kind?: string | null;
  interval?: number | string | null;
  label?: string | null;
  attachment_name?: string | null;
  completed_at?: string | null;
};

const REFUSALS: Record<string, string> = {
  not_found: 'This fueling entry no longer exists. Reload the page.',
  entry_changed: 'This service entry changed since the page loaded. Reload the page and try again.',
  invalid_value: 'That value cannot be saved.',
};
const DENIED = 'You do not have permission to edit this fueling entry.';
// How many times a no-response call is resent with the same request id.
const TRANSPORT_RETRIES = 2;

const rowQueues = new Map<string, Promise<void>>();

// Runs `task` after every earlier task for the same fueling row has settled.
function inRowOrder<T>(fuelingId: string, task: () => Promise<T>): Promise<T> {
  const previous = rowQueues.get(fuelingId) || Promise.resolve();
  const run = previous.then(task);
  const settled = run.then(
    () => undefined,
    () => undefined,
  );
  rowQueues.set(fuelingId, settled);
  void settled.then(() => {
    if (rowQueues.get(fuelingId) === settled) rowQueues.delete(fuelingId);
  });
  return run;
}

type RpcError = {message?: string; code?: string} | null | undefined;

// A PostgREST/Postgres error carries a code; a fetch that never got an answer
// does not. Only the second may be resent: the first is the database's answer.
function isTransportFailure(error: RpcError): boolean {
  return !!error && !error.code;
}

// The fueling id and the request id every audited RPC takes.
type AuditedRequest = {p_fueling_id: string; p_request_id: string};
type RpcReply = {data: unknown; error: RpcError};

// `send` names its RPC literally, so each call site stays visible to the
// physical call-site inventory; this owns the order, retry and envelope.
async function callAudited(
  ref: FuelingRef,
  send: (request: AuditedRequest) => PromiseLike<RpcReply>,
): Promise<AuditedFuelingWrite> {
  return inRowOrder(ref.fuelingId, async (): Promise<AuditedFuelingWrite> => {
    const request: AuditedRequest = {p_fueling_id: ref.fuelingId, p_request_id: newClientSubmissionId()};
    let data: unknown = null;
    let error: RpcError = null;
    for (let attempt = 0; attempt <= TRANSPORT_RETRIES; attempt++) {
      try {
        ({data, error} = await send(request));
      } catch (e) {
        error = {message: e instanceof Error ? e.message : String(e)};
      }
      if (!isTransportFailure(error)) break;
    }
    if (error) {
      if (error.code === '42501') return {ok: false, error: DENIED};
      if (isTransportFailure(error)) {
        return {
          ok: false,
          error:
            'Could not reach the server, so this change may not have saved: ' +
            (error.message || 'network error') +
            '. Reload the page to see what was saved.',
        };
      }
      return {ok: false, error: 'Save failed: ' + (error.message || 'unknown error')};
    }
    const env = (data || {}) as {ok?: boolean; reason?: string; changed?: boolean; replayed?: boolean; value?: unknown};
    if (env.ok !== true) {
      return {ok: false, error: REFUSALS[env.reason || ''] || 'Save failed: ' + (env.reason || 'unknown error')};
    }
    const changed = env.changed === true;
    const replayed = env.replayed === true;
    // An open Activity panel refreshes; the row was written by the server.
    if (changed || replayed) fireActivityChangeEvent('equipment.item', ref.equipmentId);
    return {ok: true, changed, replayed, value: env.value};
  });
}

// Sets one every-fill-up checklist item to `checked`.
export function setFuelingFillupItem(
  sb: SupabaseClient,
  ref: FuelingRef,
  item: FuelingChecklistItem,
  checked: boolean,
): Promise<AuditedFuelingWrite> {
  return callAudited(ref, (request) =>
    sb.rpc('set_equipment_fueling_check', {...request, p_item_id: String(item.id), p_checked: checked}),
  );
}

function entryIdentity(entry: FuelingIntervalEntry) {
  return {
    kind: entry.kind ?? null,
    interval: entry.interval ?? null,
    label: entry.label ?? null,
    attachment_name: entry.attachment_name ?? null,
    completed_at: entry.completed_at ?? null,
  };
}

// Sets one sub-task inside the recorded interval completion at `target.index`
// (verified against `target.entry`) to `checked`. `totalTasks` refreshes
// total_tasks from the live equipment config, as the page always did.
export function setFuelingIntervalTask(
  sb: SupabaseClient,
  ref: FuelingRef,
  target: {index: number; entry: FuelingIntervalEntry},
  taskId: string | number,
  checked: boolean,
  totalTasks: number,
): Promise<AuditedFuelingWrite> {
  return callAudited(ref, (request) =>
    sb.rpc('set_equipment_fueling_interval_task', {
      ...request,
      p_index: target.index,
      p_entry: entryIdentity(target.entry),
      p_task_id: String(taskId),
      p_checked: checked,
      p_total_tasks: totalTasks || null,
    }),
  );
}

// Removes the recorded interval entry at `target.index` (verified against
// `target.entry`) from a fueling row.
export function removeFuelingIntervalEntryAudited(
  sb: SupabaseClient,
  ref: FuelingRef,
  target: {index: number; entry: FuelingIntervalEntry},
): Promise<AuditedFuelingWrite> {
  return callAudited(ref, (request) =>
    sb.rpc('remove_equipment_fueling_interval_entry', {
      ...request,
      p_index: target.index,
      p_entry: entryIdentity(target.entry),
    }),
  );
}

// One debounced inline-editor field save (date, gallons, def_gallons,
// hours_reading, km_reading, comments). The server keeps ONE Activity row per
// edit session, however many debounced saves it takes.
export function patchFuelingField(
  sb: SupabaseClient,
  ref: FuelingRef,
  field: string,
  value: unknown,
): Promise<AuditedFuelingWrite> {
  return callAudited(ref, (request) =>
    sb.rpc('patch_equipment_fueling_field', {...request, p_field: field, p_value: value ?? null}),
  );
}

// How the page settles its optimistic copy of one column after an audited
// write. Settlement is per KEY (fueling row + column): while a later write for
// the same key is still pending, that write will settle it; once none is, a
// success adopts the stored value and a failure — a refusal, a transport
// failure or a rejected call — drops the unproven copy. Every failure is
// reported and asks for a reload. Writes under other keys never hold one back.
export type AuditedWriteHandlers = {
  adopt: (stored: unknown[]) => void;
  drop: () => void;
  fail: (message: string) => void;
};

export function createAuditedWriteSettler() {
  const inFlight = new Map<string, number>();
  return async function settle(
    key: string,
    write: () => Promise<AuditedFuelingWrite>,
    handlers: AuditedWriteHandlers,
  ): Promise<AuditedFuelingWrite> {
    inFlight.set(key, (inFlight.get(key) || 0) + 1);
    let result: AuditedFuelingWrite;
    try {
      result = await write();
    } catch (e) {
      result = {ok: false, error: 'Save failed: ' + (e instanceof Error ? e.message : String(e))};
    } finally {
      const left = (inFlight.get(key) || 1) - 1;
      if (left === 0) inFlight.delete(key);
      else inFlight.set(key, left);
    }
    const settled = !inFlight.has(key);
    if (!result.ok) {
      if (settled) handlers.drop();
      handlers.fail(result.error);
    } else if (settled && Array.isArray(result.value)) {
      handlers.adopt(result.value);
    }
    return result;
  };
}

export function intervalEntryLabel(entry: FuelingIntervalEntry): string {
  return (
    (entry.attachment_name ? entry.attachment_name + ' — ' : '') +
    (entry.label || String(entry.interval ?? '') + (entry.kind === 'km' ? 'km' : 'h'))
  );
}
